import React from 'react';
import { Header } from '../../components/Header';
import { Footer } from '../../components/Footer';
import { LocaleProvider, useLocale } from '../../i18n/LocaleContext';
import { useSeo } from '../../seo/useSeo';
import { breadcrumbJsonLd } from '../../seo/breadcrumbs';
import '../../styles/landing.css';
import '../../styles/landing-neon.css';

const CONTACT = {
  name: 'Maksym Bocharov',
  address: 'Karpatské námestie 7770/10A, 831 06, Bratislava-Rača, Slovakia',
  email: 'gather.community.org@gmail.com',
  phone: '+421 944 699 472',
};

const EFFECTIVE_DATE = 'May 5, 2026';

const TOC = [
  { id: 'collect', label: 'Information we collect' },
  { id: 'use', label: 'How we use it' },
  { id: 'auth', label: 'Authentication' },
  { id: 'location', label: 'Location data' },
  { id: 'ai', label: 'AI features' },
  { id: 'comms', label: 'Push & email' },
  { id: 'payments', label: 'Payments' },
  { id: 'subprocessors', label: 'Sub-processors' },
  { id: 'transfers', label: 'International transfers' },
  { id: 'retention', label: 'Data retention' },
  { id: 'install-links', label: 'Event install links' },
  { id: 'sharing', label: 'Data sharing' },
  { id: 'security', label: 'Security' },
  { id: 'rights', label: 'Your rights (GDPR)' },
  { id: 'children', label: 'Children' },
  { id: 'changes', label: 'Changes' },
  { id: 'contact', label: 'Contact' },
];

const PillarIcon = ({ children }: { children: React.ReactNode }): React.JSX.Element => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    {children}
  </svg>
);

const PILLARS: { icon: React.ReactNode; title: string; body: string }[] = [
  {
    icon: (
      <PillarIcon>
        <path d="M12 3l3 6 6 .9-4.5 4.2 1.1 6.3L12 17.8 6.4 20.4l1.1-6.3L3 9.9 9 9z" />
      </PillarIcon>
    ),
    title: "We don't sell data",
    body: 'Your data is never sold to anyone. Ever.',
  },
  {
    icon: (
      <PillarIcon>
        <circle cx="12" cy="12" r="9" />
        <path d="M3 12h18M12 3a13 13 0 0 1 0 18M12 3a13 13 0 0 0 0 18" />
      </PillarIcon>
    ),
    title: 'EU-based servers',
    body: 'Hosted on Hetzner Cloud in Germany.',
  },
  {
    icon: (
      <PillarIcon>
        <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M5 6l1 14a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2l1-14M10 11v6M14 11v6" />
      </PillarIcon>
    ),
    title: 'Easy to delete',
    body: 'Tap Delete in the app — your account is gone right away.',
  },
  {
    icon: (
      <PillarIcon>
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <path d="M14 2v6h6M8 13h8M8 17h6" />
      </PillarIcon>
    ),
    title: 'Plain language',
    body: 'No legalese where we can avoid it.',
  },
];

const SUBPROCESSORS = [
  { name: 'Hetzner Online GmbH', region: 'Germany', purpose: 'App backend & PostgreSQL database' },
  { name: 'Cloudflare, Inc.', region: 'US / global edge', purpose: 'File storage (R2): avatars, photos, attachments' },
  { name: 'Google LLC', region: 'United States', purpose: 'Firebase Cloud Messaging, Firebase Analytics, Google Sign-In, Gmail SMTP' },
  { name: 'Apple Inc.', region: 'United States', purpose: 'Sign in with Apple' },
  { name: 'OpenAI, L.L.C.', region: 'United States', purpose: 'AI features (only when you use them)' },
];

const RETENTION = [
  { label: 'Account data', window: 'Immediate', note: 'Removed from production systems the moment you tap Delete in the app' },
  { label: 'Backups', window: 'Up to 90 days', note: 'Encrypted backups are overwritten within 90 days of deletion' },
  { label: 'Server access logs', window: 'Up to 90 days', note: 'IP addresses and request timestamps' },
  { label: 'Email verification codes', window: 'Minutes', note: 'Deleted shortly after use or expiry' },
  { label: 'Reports & moderation', window: 'Up to 2 years', note: 'For safety and legal reasons' },
];

const PrivacyInner = (): React.JSX.Element => {
  const { t } = useLocale();

  useSeo({
    title: 'Privacy Policy',
    description:
      "How Gather handles your data: what we collect, why, where it lives (EU servers), and your GDPR rights. We don't sell your data — ever.",
    canonical: '/privacy',
    jsonLd: breadcrumbJsonLd([
      { name: 'Home', path: '/' },
      { name: 'Privacy', path: '/privacy' },
    ]),
  });

  return (
    <div className="landing landing--neon" id="top">
      <Header />
      <main>
        <section className="privacy-page">
          <div className="wrap privacy-wrap">
            <aside className="privacy-toc">
              <div className="privacy-toc-inner">
                <span className="privacy-toc-label">On this page</span>
                <ol>
                  {TOC.map((item, i) => (
                    <li key={item.id}>
                      <a href={`#${item.id}`}>
                        <span className="toc-num">{String(i + 1).padStart(2, '0')}</span>
                        <span className="toc-text">{item.label}</span>
                      </a>
                    </li>
                  ))}
                </ol>
              </div>
            </aside>

            <article className="privacy-content">
              <header className="privacy-hero">
                <span className="eyebrow">{t('privacy.eyebrow')}</span>
                <h1>Privacy Policy</h1>
                <div className="privacy-meta-row">
                  <span className="privacy-tag">Effective {EFFECTIVE_DATE}</span>
                  <span className="privacy-tag">~ 6 min read</span>
                  <span className="privacy-tag">EN</span>
                </div>
                <p className="privacy-intro">
                  This page explains how the Gather mobile app and website collect, use, and protect
                  your personal data. We aim to be straightforward — collect only what we need,
                  store it where we trust, and give you full control.
                </p>
              </header>

              <div className="privacy-pillars">
                {PILLARS.map((p) => (
                  <div className="pillar" key={p.title}>
                    <div className="pillar-icon">{p.icon}</div>
                    <h3>{p.title}</h3>
                    <p>{p.body}</p>
                  </div>
                ))}
              </div>

              <div className="legal-card">
                <h2 style={{ fontSize: 22, marginBottom: 8, marginTop: 0 }}>{t('privacy.controller.h')}</h2>
                <p style={{ color: '#4a4255', marginBottom: 16 }}>{t('privacy.controller.p')}</p>
                <dl className="legal-dl">
                  <dt>{t('privacy.field.name')}</dt>
                  <dd>{CONTACT.name}</dd>
                  <dt>{t('privacy.field.address')}</dt>
                  <dd>{CONTACT.address}</dd>
                  <dt>{t('privacy.field.email')}</dt>
                  <dd><a href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a></dd>
                  <dt>{t('privacy.field.phone')}</dt>
                  <dd><a href={`tel:${CONTACT.phone.replace(/\s+/g, '')}`}>{CONTACT.phone}</a></dd>
                </dl>
              </div>

              <Section id="collect" num={1} title="Information we collect">
                <p>We collect the following categories of data when you use Gather:</p>
                <Sub title="a) Account information">
                  <ul>
                    <li>Name and surname</li>
                    <li>Email address</li>
                    <li>Authentication identifiers from Google or Apple Sign-In (when you choose them)</li>
                    <li>One-time email verification codes during sign-up and sign-in</li>
                  </ul>
                </Sub>
                <Sub title="b) Profile information">
                  <ul>
                    <li>Profile photo (avatar)</li>
                    <li>Date of birth</li>
                    <li>City, address, or location information you choose to share</li>
                    <li>Interests, preferences, and clubs you join</li>
                  </ul>
                </Sub>
                <Sub title="c) Content you create">
                  <ul>
                    <li>Events you host or join, including title, description, photos, and location</li>
                    <li>Posts, comments, likes, and reposts</li>
                    <li>Direct messages and group chat content</li>
                    <li>Reports and feedback you submit</li>
                  </ul>
                </Sub>
                <Sub title="d) Location data">
                  <ul>
                    <li>Approximate or precise location, used to surface events near you</li>
                    <li>Requested only when needed and revocable in device settings at any time</li>
                  </ul>
                </Sub>
                <Sub title="e) Usage and device data">
                  <ul>
                    <li>Device type, operating system, app version, and language</li>
                    <li>Crash logs and basic analytics events (screen views, button taps)</li>
                    <li>IP address and timestamps of requests, captured in server access logs</li>
                    <li>Push notification token, if you allow notifications</li>
                  </ul>
                </Sub>
                <Sub title="f) Communications">
                  <ul>
                    <li>Emails you send to us (e.g., support requests)</li>
                    <li>In-app feedback and bug reports</li>
                  </ul>
                </Sub>
              </Section>

              <Section id="use" num={2} title="How we use your information">
                <p>We use your data for the following purposes, with the GDPR legal basis in brackets:</p>
                <ul className="ll-tagged">
                  <li>Operate and maintain the Service, including managing your account <em>[contract]</em></li>
                  <li>Show you events, clubs, and people relevant to your location and interests <em>[contract / legitimate interest]</em></li>
                  <li>Enable participation, hosting, chat, and posts <em>[contract]</em></li>
                  <li>Send service emails and push notifications you opt in to <em>[contract / consent]</em></li>
                  <li>Detect abuse, enforce community rules, and keep the platform safe <em>[legitimate interest / legal obligation]</em></li>
                  <li>Improve the Service and fix bugs using anonymized analytics <em>[legitimate interest]</em></li>
                  <li>Comply with applicable laws and respond to lawful requests <em>[legal obligation]</em></li>
                </ul>
              </Section>

              <Section id="auth" num={3} title="Authentication">
                <p>You can sign in with:</p>
                <ul>
                  <li>Email and a one-time code (sent via Gmail SMTP)</li>
                  <li>Google Sign-In</li>
                  <li>Apple Sign-In</li>
                </ul>
                <p>
                  When you sign in with Google or Apple, we receive only the basic profile data
                  those providers share (typically name and email). We do not receive your password.
                </p>
                <p>
                  Sessions are maintained using JSON Web Tokens (JWT) and refresh tokens stored on
                  your device — not as third-party cookies in a browser.
                </p>
              </Section>

              <Section id="location" num={4} title="Location data">
                <p>We use your location to:</p>
                <ul>
                  <li>Show events and clubs nearby</li>
                  <li>Improve recommendations</li>
                  <li>Prefill the location field when you create an event (with your permission)</li>
                </ul>
                <p>
                  Location is requested only when needed. You can revoke location permission at any
                  time in your device settings, and the rest of the app continues to work without it.
                </p>
              </Section>

              <Section id="ai" num={5} title="AI-powered features">
                <p>
                  Some Gather features (for example, content suggestions, summaries, or auto-fill of
                  event descriptions) are powered by OpenAI. When you use these features, the
                  relevant text you provide is sent to OpenAI for processing and a response is
                  returned to you.
                </p>
                <ul>
                  <li>We do not send your password, payment details, or precise location to OpenAI.</li>
                  <li>OpenAI's API terms prohibit using API inputs to train their public models by default.</li>
                  <li>If you do not want your content processed this way, simply do not use the AI features.</li>
                </ul>
              </Section>

              <Section id="comms" num={6} title="Push notifications and email">
                <p>
                  We use Firebase Cloud Messaging to deliver push notifications. You can disable
                  push notifications in your device settings or in the app at any time.
                </p>
                <p>
                  We use Gmail SMTP to send transactional emails (verification codes, account
                  notices). We do not send marketing emails without your explicit consent.
                </p>
              </Section>

              <Section id="payments" num={7} title="Payments">
                <p>
                  Gather does not process payments inside the app. Where an event is paid, you
                  arrange payment with the organizer directly (for example, in cash or via their
                  preferred method). We do not collect, store, or transmit payment card information.
                </p>
              </Section>

              <Section id="subprocessors" num={8} title="Sub-processors and third parties">
                <p>To run the Service we rely on the following providers:</p>
                <div className="sp-grid">
                  {SUBPROCESSORS.map((sp) => (
                    <div className="sp-card" key={sp.name}>
                      <div className="sp-name">{sp.name}</div>
                      <div className="sp-region">{sp.region}</div>
                      <div className="sp-purpose">{sp.purpose}</div>
                    </div>
                  ))}
                </div>
                <p style={{ marginTop: 16 }}>
                  These providers act as our data processors or independent controllers under their
                  own terms. We choose providers we believe offer appropriate security and
                  contractual guarantees.
                </p>
              </Section>

              <Section id="transfers" num={9} title="International data transfers">
                <p>
                  Our primary servers and database are located in the European Union (Germany, on
                  Hetzner). Some sub-processors are based in the United States or operate globally.
                  Where personal data is transferred outside the EU/EEA, we rely on the European
                  Commission's Standard Contractual Clauses (SCCs) and, where applicable, the
                  EU-U.S. Data Privacy Framework.
                </p>
              </Section>

              <Section id="retention" num={10} title="Data retention">
                <p>We keep data only for as long as we need it:</p>
                <div className="ret-grid">
                  {RETENTION.map((r) => (
                    <div className="ret-row" key={r.label}>
                      <div className="ret-label">{r.label}</div>
                      <div className="ret-window">{r.window}</div>
                      <div className="ret-note">{r.note}</div>
                    </div>
                  ))}
                </div>
                <p style={{ marginTop: 16 }}>
                  When data is deleted, it is removed from our active databases. Residual copies
                  may persist in encrypted backups for the period above before being overwritten.
                </p>
              </Section>

              <Section id="install-links" num={11} title="Event install links">
                <p>
                  When you tap a shared event link and choose to install the app, we briefly
                  store an "install intent" so the app can open that exact event the first time
                  you launch it. It contains the event id, a salted hash of your IP address, and
                  coarse device characteristics: screen size, locale, timezone, and OS version.
                </p>
                <ul>
                  <li>Kept for a maximum of 60 minutes, then deleted automatically</li>
                  <li>Used solely to open the shared event after you install the app</li>
                  <li>Never shared with anyone, and never used for advertising or tracking</li>
                </ul>
              </Section>

              <Section id="sharing" num={12} title="Data sharing">
                <p>We do not sell your personal data. We share data only:</p>
                <ul>
                  <li>With the sub-processors listed above, strictly to operate the Service</li>
                  <li>With other Gather users, when you choose to share — your profile, posts, and events you host or attend are visible to people you have shared them with</li>
                  <li>If required by law, court order, or to protect rights, safety, or the integrity of the Service</li>
                  <li>If we are involved in a merger, acquisition, or sale of assets — and only after notifying you</li>
                </ul>
              </Section>

              <Section id="security" num={13} title="Security">
                <ul>
                  <li>All traffic between your device and our servers is encrypted with TLS</li>
                  <li>Passwords are hashed; we never store them in plain text</li>
                  <li>Access to production systems is restricted and audited</li>
                  <li>We use isolated dev and prod databases and follow safe schema-update practices</li>
                </ul>
                <p>
                  No system is perfectly secure. If you believe your account has been compromised,
                  contact us right away.
                </p>
              </Section>

              <Section id="rights" num={14} title="Your rights (GDPR)">
                <p>If you are in the EEA, the United Kingdom, or Switzerland, you have the right to:</p>
                <ul>
                  <li>Access the personal data we hold about you</li>
                  <li>Correct data that is inaccurate or incomplete</li>
                  <li>Delete your data ("right to be forgotten")</li>
                  <li>Restrict or object to processing in certain cases</li>
                  <li>Receive your data in a portable format</li>
                  <li>Withdraw any consent you have given, at any time</li>
                  <li>Lodge a complaint with a supervisory authority — for Slovakia, the Office for Personal Data Protection of the Slovak Republic (<em>Úrad na ochranu osobných údajov SR</em>)</li>
                </ul>
                <p>
                  To exercise any of these rights, email{' '}
                  <a href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a>. We respond within 30 days.
                </p>
                <h3 style={{ marginTop: 24 }}>Deleting your account</h3>
                <p>
                  You can delete your account at any time from inside the app (Profile → Settings →
                  Delete account), or by emailing us. Account deletion removes your profile and
                  personal content from production systems immediately, and from encrypted backups
                  within 90 days.
                </p>
              </Section>

              <Section id="children" num={15} title="Children's privacy">
                <p>
                  Gather is intended for users aged 16 and over. We do not knowingly collect
                  personal data from children under 13. If you believe a child has provided
                  personal data, please contact us and we will delete it.
                </p>
              </Section>

              <Section id="changes" num={16} title="Changes to this policy">
                <p>
                  We may update this Privacy Policy from time to time. When we make material
                  changes, we will update the "Effective date" above and, where appropriate, notify
                  you in the app or by email. Continued use of the Service after the update means
                  you accept the revised policy.
                </p>
              </Section>

              <Section id="contact" num={17} title="Contact">
                <p>
                  For any privacy question or request, email{' '}
                  <a href={`mailto:${CONTACT.email}`} style={{ color: 'var(--purple-600)', fontWeight: 600 }}>
                    {CONTACT.email}
                  </a>{' '}
                  or write to us at the postal address in the data controller card above.
                </p>
              </Section>

              <p className="legal-operator">Operator: {CONTACT.name}</p>
            </article>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

type SectionProps = {
  id: string;
  num: number;
  title: string;
  children: React.ReactNode;
};

const Section = ({ id, num, title, children }: SectionProps): React.JSX.Element => (
  <section id={id} className="privacy-section">
    <h2>
      <span className="privacy-section-num">{String(num).padStart(2, '0')}</span>
      <span>{title}</span>
    </h2>
    {children}
  </section>
);

type SubProps = { title: string; children: React.ReactNode };
const Sub = ({ title, children }: SubProps): React.JSX.Element => (
  <div className="privacy-sub">
    <h3>{title}</h3>
    {children}
  </div>
);

export const Privacy = (): React.JSX.Element => (
  <LocaleProvider>
    <PrivacyInner />
  </LocaleProvider>
);
